// PROGRESSIV OPPGRADERING: fri tekst → strukturert stridsmelding.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// UX-gjennomgang 2026-08-18, rapportens **Fase 2** (løser funn F3 og F1):
//
//   «Alt starter likt: et punkt + fri tekst eller tale (dagens Feltmelding/PTT
//   blir standardveien). Trenger du struktur, trykker du en chip — SALUTE, TIC,
//   9-liner, SITREP — og de riktige feltene folder seg ut inni samme melding.
//   Posisjon, DTG og avsender er allerede fylt ut. Struktur er en oppgradering,
//   ikke et hinder.»
//
// F3 er «skjema først, kart sist»; F1 er «samme oppgave, fem verktøy». Begge
// løses av at operatøren aldri MÅ velge type før hun begynner å skrive.
//
// ── ⚠ SPØRSMÅLET SOM AVGJØR OM DETTE ER TRYGT ──────────────────────────────
// Når en operatør har skrevet «to kjøretøy sørover på 156 ved Vinterbro» og
// trykker «+ SALUTE» — HVOR havner setningen?
//
// Det gale svaret er å slenge den i det første feltet som finnes. «Størrelse (S)»
// fylt med en hel setning er en melding som ser strukturert ut og ikke er det, og
// en mottaker som leser S-feltet får noe annet enn det observatøren mente.
//
// Det riktige svaret er FRITEKSTFELTET til hver meldingstype — feltet som ikke
// hevder å være strukturert. Operatørens ord står da ORDRETT et sted de hører
// hjemme, og hun fyller S/A/L/U/T/E over med de tallene og ordene som skal dit.
// Ingenting blir omskrevet, ingenting blir gjettet, ingenting blir borte.
//
// ── ⚠ MEDEVAC HAR INGEN FRITEKST, OG DET SKAL DEN IKKE FÅ ──────────────────
// 9-lineren er L1–L9 og ingenting annet. Å legge til et notatfelt for å få denne
// funksjonen til å gå opp ville vært å endre et NATO-meldingsformat for vår egen
// bekvemmelighets skyld. Derfor svarer denne fila `felt: null` for MEDEVAC, og
// flata SKAL da vise teksten synlig ved siden av skjemaet så operatøren kan føre
// den over i de riktige linjene. Teksten forsvinner ikke — den blir ikke plassert
// automatisk, som er noe helt annet.
import { REPORT_FIELDS } from './report.js';
/**
 * Fritekstfeltet per meldingstype — der operatørens egne ord kan stå ORDRETT.
 *
 * `null` betyr «denne meldingstypen har ikke noe sted for fri tekst». Det er en
 * egenskap ved formatet, ikke en mangel her.
 */
export const FRITEKSTFELT = {
    // «HVA (hendelse)» er selve fritekstfeltet i Forsvarets frie stridsmelding.
    MELDING: 'hva',
    // ⚠ «Tillegg», IKKE «Aktivitet (A)». En blandet setning inneholder som regel
    // størrelse, aktivitet OG sted på én gang; legger vi den i A-feltet, påstår vi
    // at hele setningen er aktiviteten. `extra` er eksplisitt et vedlegg (se
    // kommentaren over SALUTE i report.ts) og lyver derfor ikke.
    SALUTE: 'extra',
    // «Kontaktmelding» er fritekst og det første feltet i formatet — en TIC skrevet
    // i full fart hører nettopp her.
    CONTACT: 'note',
    // «Situasjon», ikke «Oppdrag/status»: fri tekst beskriver som regel hva som
    // SKJER, ikke hva avdelingen er satt til å gjøre.
    SITREP: 'situation',
    // ⚠ INGEN. 9-lineren er L1–L9. Se filhodet.
    MEDEVAC: null,
    UXO: 'description',
    // Etiketten er «Tiltak/melding» — «melding» står der, så feltet ER ment å
    // kunne bære fritekst.
    CBRN: 'action',
};
/** Meldingstypene som tilbys som oppgraderings-chip, i rekkefølge. */
export const OPPGRADERINGER = [
    'CONTACT', 'SALUTE', 'MEDEVAC', 'SITREP', 'UXO', 'CBRN', 'MELDING',
];
/** Kort chip-etikett. Kodeordene står uoversatt, som i resten av systemet. */
export const OPPGRADERING_ETIKETT = {
    CONTACT: 'TIC',
    SALUTE: 'SALUTE',
    MEDEVAC: '9-liner',
    SITREP: 'SITREP',
    UXO: 'UXO',
    CBRN: 'CBRN',
    MELDING: 'Stridsmelding',
};
/**
 * Oppgrader fri tekst til en meldingstype.
 *
 * Rene data ut — ingen DOM, ingen bivirkning. Flata bruker `felt`/`tekst` til å
 * forhåndsutfylle skjemaet og `forklaring` til å si det høyt.
 */
export function oppgraderFritekst(type, tekst) {
    const t = tekst.trim();
    const felt = FRITEKSTFELT[type];
    if (!felt) {
        return {
            type, felt: null, feltLabel: '', tekst: t,
            // ⚠ SIER HVA OPERATØREN SKAL GJØRE. «Har ikke fritekstfelt» alene er en
            // blindvei; hun må vite at teksten står trygt og hva neste steg er.
            forklaring: t
                ? '9-lineren har bare linjene L1–L9 og ingen fritekst. Teksten din står '
                    + 'urørt over skjemaet — før den inn i de linjene den hører til.'
                : '9-lineren fylles linje for linje (L1–L9).',
        };
    }
    const def = REPORT_FIELDS[type].find((f) => f.key === felt);
    const feltLabel = def?.label ?? felt;
    return {
        type, felt, feltLabel, tekst: t,
        forklaring: t
            ? `Teksten din står ordrett i «${feltLabel}». Fyll ut de øvrige feltene — `
                + 'ingenting av det du skrev er endret.'
            : `Fri tekst havner i «${feltLabel}».`,
    };
}
/**
 * Feltene operatøren bør fylle etter en oppgradering — alt utenom fritekstfeltet
 * og arvefelt.
 *
 * Finnes for at flata skal kunne si «4 felt igjen» framfor å presentere et
 * skjema der man ikke ser hva som er gjort. Arvefelt (`legacy`) tas ut fordi de
 * aldri tilbys som inndata.
 */
export function gjenstaaendeFelt(type) {
    const fri = FRITEKSTFELT[type];
    return REPORT_FIELDS[type]
        .filter((f) => !f.legacy && f.key !== fri)
        .map((f) => f.key);
}
//# sourceMappingURL=oppgradering.js.map